// PDP Variant Selector - Handle gallery image switching for variant selection
// Integrates with SparkLayer B2B and standard Shopify variant selection

class PDPVariantSelector {
  constructor() {
    this.productSection = document.querySelector('.wt-product__wrapper');
    if (!this.productSection) return;

    this.gallerySection = this.productSection.querySelector('gallery-section');
    this.variantData = null;
    this.currentVariantId = null;
    
    this.init();
  }

  init() {
    // Get variant data from the variant-options script
    const variantScript = this.productSection.querySelector('variant-options script[type="application/json"]');
    if (variantScript) {
      try {
        this.variantData = JSON.parse(variantScript.textContent);
      } catch (e) {
        console.error('Failed to parse variant data:', e);
      }
    }

    // Get initially selected variant ID
    const productForm = this.productSection.querySelector('product-form form');
    if (productForm) {
      const variantInput = productForm.querySelector('input[name="id"]');
      if (variantInput) {
        this.currentVariantId = variantInput.value;
      }
    }

    this.bindEvents();
  }

  bindEvents() {
    // Listen for B2C variant selection changes (radio inputs)
    document.addEventListener('change', (e) => {
      // Check if this is a variant option radio button within our product section
      if (e.target.type === 'radio' && 
          e.target.closest('.wt-product__wrapper') === this.productSection &&
          e.target.closest('variant-options')) {
        
        // Small delay to allow variant-options to update the hidden input
        setTimeout(() => {
          this.handleVariantChange();
        }, 50);
      }
    });

    // Listen for SparkLayer B2B variant changes using the correct event
    const sparkPdp = this.productSection.querySelector('spark-pdp');
    if (sparkPdp) {
      sparkPdp.addEventListener('spark-variant-change', (e) => {
        if (e.detail && e.detail.variant && e.detail.variant.externalId) {
          this.handleVariantChange(e.detail.variant.externalId);
        }
      });
    }

    // Also listen for the product form hidden input changes as a fallback
    const productForm = this.productSection?.querySelector('product-form form');
    if (productForm) {
      const variantInput = productForm.querySelector('input[name="id"]');
      if (variantInput) {
        // Use MutationObserver to watch for value changes
        const observer = new MutationObserver(() => {
          const newVariantId = variantInput.value;
          if (newVariantId && newVariantId !== this.currentVariantId) {
            this.handleVariantChange(newVariantId);
          }
        });
        
        observer.observe(variantInput, {
          attributes: true,
          attributeFilter: ['value']
        });
      }
    }
  }

  handleVariantChange(variantId = null) {
    // Get the variant ID from the form if not provided
    if (!variantId) {
      const productForm = this.productSection?.querySelector('product-form form');
      if (!productForm) return;
      
      const variantInput = productForm.querySelector('input[name="id"]');
      if (!variantInput) return;
      
      variantId = variantInput.value;
    }

    // Don't update if it's the same variant
    if (variantId === this.currentVariantId) return;
    
    this.currentVariantId = variantId;

    // Find the variant in our data
    if (!this.variantData || !Array.isArray(this.variantData)) return;
    
    const selectedVariant = this.variantData.find(v => v.id.toString() === variantId.toString());
    if (!selectedVariant) return;

    // Get the featured media ID
    const featuredMediaId = selectedVariant.featured_media?.id;
    if (!featuredMediaId) return;

    // Update the gallery
    this.updateGallery(featuredMediaId);
  }

  updateGallery(mediaId) {
    if (!this.gallerySection) return;

    // Use the GallerySection's built-in setActiveMedia method
    // This properly handles both the main gallery and thumbnails
    // and works with all gallery layouts (carousel, masonry, collage)
    if (typeof this.gallerySection.setActiveMedia === 'function') {
      this.gallerySection.setActiveMedia(mediaId.toString(), true);
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new PDPVariantSelector();
  });
} else {
  new PDPVariantSelector();
}

// Also reinitialize if sections are re-rendered (e.g., via Shopify section rendering)
document.addEventListener('shopify:section:load', () => {
  new PDPVariantSelector();
});

// Integration with SparkLayer SDK onReady callback (if available)
// This ensures the spark-pdp element is fully loaded before we try to attach listeners
if (window.sparkOptions && typeof window.sparkOptions === 'object') {
  const originalOnReady = window.sparkOptions.onReady;
  window.sparkOptions.onReady = function() {
    // Call original onReady if it exists
    if (typeof originalOnReady === 'function') {
      originalOnReady.call(this);
    }
    // Initialize our selector
    new PDPVariantSelector();
  };
} else if (typeof window !== 'undefined') {
  // If sparkOptions doesn't exist yet, create it
  window.sparkOptions = window.sparkOptions || {};
  window.sparkOptions.onReady = function() {
    new PDPVariantSelector();
  };
}
